import React, { CSSProperties, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';

type TGoogleMapProps = {
  locations: TLocations[] | undefined;
  containerCssStyle?: CSSProperties;
  zoom?: number;
  isPinDraggable?: boolean;
  // eslint-disable-next-line no-undef
  onDragEnd?: ((e: google.maps.MapMouseEvent) => void) | undefined;
};
type TLocations = { lat: number; lng: number };

const isValidLocation = (location?: TLocations): location is TLocations =>
  location != null &&
  Number.isFinite(location.lat) &&
  Number.isFinite(location.lng) &&
  (location.lat !== 0 || location.lng !== 0);

export const GoogleMapComponent = ({
  locations,
  containerCssStyle,
  zoom,
  isPinDraggable,
  onDragEnd,
}: TGoogleMapProps) => {
  // eslint-disable-next-line no-undef
  const mapRef = useRef<google.maps.Map | null>(null);

  const validLocations = useMemo(() => locations?.filter(isValidLocation) ?? [], [locations]);

  const containerStyle = {
    width: '100%',
    height: '18.75rem',
    ...containerCssStyle,
  };

  const onLoad = useCallback(
    // eslint-disable-next-line no-undef
    (map: google.maps.Map) => {
      if (validLocations.length === 1) return;

      mapRef.current = map;

      // Auto-fit bounds to all markers
      const bounds = new window.google.maps.LatLngBounds();
      validLocations.forEach(loc => bounds.extend(loc));
      map.fitBounds(bounds);
    },
    [validLocations],
  );

  if (validLocations.length < 1) return null;

  const center = validLocations.length === 1 ? validLocations[0] : undefined;

  return (
    <>
      {validLocations.length > 1 && (
        <GoogleMap mapContainerStyle={containerStyle} onLoad={onLoad}>
          {validLocations.map((location: TLocations, index) => {
            return <Marker key={index} position={location} />;
          })}
        </GoogleMap>
      )}

      {validLocations.length === 1 && center && (
        <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={zoom || 12}>
          <Marker position={center} draggable={isPinDraggable} onDragEnd={onDragEnd} />
        </GoogleMap>
      )}
    </>
  );
};
